import { LitElement, html, css, type PropertyValues } from "lit";
import { customElement, state } from "lit/decorators.js";
import { shared } from "../styles.js";
import {
  mfaEnabled,
  totpEnroll,
  totpActivate,
  totpDisable,
  listPasskeys,
  registerPasskey,
  deletePasskey,
  passkeySupported,
  type PasskeyInfo,
} from "../api/account.js";
import { humanError } from "../api/errors.js";

/**
 * settings-security manages the account's second factors: passkeys (WebAuthn)
 * and authenticator-app TOTP. Passkeys list/add/remove; TOTP moves through
 * disabled → enrolling (QR code + manual key + first-code activation) → enabled
 * (disable requires a current code). The QR is rendered client-side from the
 * otpauth:// URI — the `qrcode` package is dynamically imported so it lands in its
 * own chunk. The QR canvas sits on a white inset so it scans reliably in dark
 * theme.
 */
@customElement("settings-security")
export class SettingsSecurity extends LitElement {
  static override styles = [
    shared,
    css`
      .qr {
        display: inline-block;
        background: #fff;
        padding: 12px;
        border-radius: var(--radius, 8px);
        margin: var(--sp-3) 0;
      }
      .code-input {
        font-family: var(--mono);
        font-size: var(--fs-4);
        letter-spacing: 0.4em;
        text-align: center;
        max-width: 200px;
      }
      .manual {
        word-break: break-all;
      }
      .state-line {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: var(--sp-2) 0;
      }
      form {
        margin-top: var(--sp-3);
      }
      .pk-list {
        list-style: none;
        margin: var(--sp-2) 0;
        padding: 0;
      }
      .pk-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 8px 0;
        border-bottom: 1px solid var(--border);
      }
      .pk-row:last-child {
        border-bottom: none;
      }
      .pk-add {
        display: flex;
        gap: 8px;
        align-items: flex-end;
        margin-top: var(--sp-3);
        flex-wrap: wrap;
      }
      .pk-add .field {
        margin: 0;
        flex: 1 1 180px;
      }
    `,
  ];

  @state() private passkeys: PasskeyInfo[] = [];
  @state() private passkeyName = "";
  @state() private passkeyBusy = false;
  @state() private passkeyError = "";
  @state() private enabled = mfaEnabled();
  @state() private enrolling = false;
  @state() private secret = "";
  @state() private otpauthUrl = "";
  @state() private code = "";
  @state() private disableCode = "";
  @state() private errorMsg = "";
  @state() private notice = "";
  @state() private busy = false;

  override updated(changed: PropertyValues) {
    if ((changed.has("otpauthUrl") || changed.has("enrolling")) && this.enrolling && this.otpauthUrl) {
      void this.paintQR();
    }
  }

  private async paintQR() {
    const canvas = this.renderRoot.querySelector("canvas");
    if (!canvas) return;
    try {
      // qrcode is CommonJS: the bundled namespace carries the API on .default.
      const mod: unknown = await import("qrcode");
      const QRCode = ((mod as { default?: unknown }).default ?? mod) as typeof import("qrcode");
      await QRCode.toCanvas(canvas, this.otpauthUrl, { width: 192, margin: 1 });
    } catch (err) {
      // The manual key below the canvas keeps enrollment possible without the QR.
      console.error("QR render failed", err);
    }
  }

  override connectedCallback() {
    super.connectedCallback();
    if (passkeySupported()) void this.loadPasskeys();
  }

  private async loadPasskeys() {
    try {
      this.passkeys = await listPasskeys();
    } catch (err) {
      this.passkeyError = humanError(err);
    }
  }

  private async addPasskey() {
    this.passkeyError = "";
    this.passkeyBusy = true;
    try {
      const name = this.passkeyName.trim() || defaultPasskeyName();
      await registerPasskey(name);
      this.passkeyName = "";
      await this.loadPasskeys();
    } catch (err) {
      // A user-cancelled ceremony throws too; keep the message soft.
      this.passkeyError = "Could not add the passkey. It may have been cancelled — try again.";
      console.debug("passkey registration failed", err);
    } finally {
      this.passkeyBusy = false;
    }
  }

  private async removePasskey(id: string) {
    this.passkeyError = "";
    this.passkeyBusy = true;
    try {
      await deletePasskey(id);
      await this.loadPasskeys();
    } catch (err) {
      this.passkeyError = humanError(err);
    } finally {
      this.passkeyBusy = false;
    }
  }

  private async startEnroll() {
    this.errorMsg = "";
    this.notice = "";
    this.busy = true;
    try {
      const res = await totpEnroll();
      this.secret = res.secret;
      this.otpauthUrl = res.otpauthUrl;
      this.code = "";
      this.enrolling = true;
    } catch (err) {
      this.errorMsg = humanError(err);
    } finally {
      this.busy = false;
    }
  }

  private async activate(e: Event) {
    e.preventDefault();
    this.errorMsg = "";
    this.busy = true;
    try {
      await totpActivate(this.code.trim());
      this.enabled = true;
      this.enrolling = false;
      this.secret = "";
      this.otpauthUrl = "";
      this.notice = "Two-factor authentication is enabled.";
    } catch (err) {
      this.errorMsg = humanError(err);
    } finally {
      this.busy = false;
    }
  }

  private async disable(e: Event) {
    e.preventDefault();
    this.errorMsg = "";
    this.notice = "";
    this.busy = true;
    try {
      await totpDisable(this.disableCode.trim());
      this.enabled = false;
      this.disableCode = "";
      this.notice = "Two-factor authentication is disabled.";
    } catch (err) {
      this.errorMsg = humanError(err);
    } finally {
      this.busy = false;
    }
  }

  private cancelEnroll() {
    this.enrolling = false;
    this.secret = "";
    this.otpauthUrl = "";
    this.code = "";
    this.errorMsg = "";
  }

  override render() {
    return html`
      ${this.renderPasskeys()}
      <div class="card">
        <h2>Two-factor authentication</h2>
        ${this.errorMsg ? html`<div class="error" role="alert">${this.errorMsg}</div>` : ""}
        ${this.notice ? html`<div class="card" style="border-color:var(--pos)">${this.notice}</div>` : ""}
        ${this.enabled ? this.renderEnabled() : this.enrolling ? this.renderEnrolling() : this.renderDisabled()}
      </div>
    `;
  }

  private renderPasskeys() {
    if (!passkeySupported()) return "";
    return html`
      <div class="card">
        <h2>Passkeys</h2>
        <p class="muted">
          Sign in without a code using your device's biometrics or security key. Passkeys are
          phishing-resistant and never require an emailed code.
        </p>
        ${this.passkeyError ? html`<div class="error" role="alert">${this.passkeyError}</div>` : ""}
        ${this.passkeys.length
          ? html`<ul class="pk-list">
              ${this.passkeys.map(
                (p) => html`<li class="pk-row">
                  <span>${p.name}</span>
                  <button
                    class="btn btn-danger"
                    ?disabled=${this.passkeyBusy}
                    @click=${() => this.removePasskey(p.id)}
                  >
                    Remove
                  </button>
                </li>`,
              )}
            </ul>`
          : html`<p class="muted">No passkeys registered yet.</p>`}
        <div class="pk-add">
          <div class="field">
            <label for="pk-name">Name <span class="muted">(optional)</span></label>
            <input
              id="pk-name"
              placeholder="e.g. MacBook Touch ID"
              .value=${this.passkeyName}
              @input=${(e: Event) => (this.passkeyName = (e.target as HTMLInputElement).value)}
            />
          </div>
          <button class="btn btn-primary" ?disabled=${this.passkeyBusy} @click=${this.addPasskey}>
            ${this.passkeyBusy ? "Waiting…" : "Add a passkey"}
          </button>
        </div>
      </div>
    `;
  }

  private renderDisabled() {
    return html`
      <p class="muted">
        Protect your account with an authenticator app (TOTP). After setup, signing in requires a
        6-digit code in addition to your emailed sign-in code.
      </p>
      <button class="btn btn-primary" ?disabled=${this.busy} @click=${this.startEnroll}>
        ${this.busy ? "Preparing…" : "Set up authenticator app"}
      </button>
    `;
  }

  private renderEnrolling() {
    return html`
      <p class="muted">Scan this QR code with your authenticator app:</p>
      <div class="qr"><canvas></canvas></div>
      <p class="muted manual">
        Can't scan? Enter this key manually: <span class="mono">${this.secret}</span>
      </p>
      <form @submit=${this.activate}>
        <div class="field">
          <label for="activate-code">Enter the 6-digit code to activate</label>
          <input
            id="activate-code"
            class="code-input"
            inputmode="numeric"
            autocomplete="one-time-code"
            pattern="[0-9]{6}"
            maxlength="6"
            .value=${this.code}
            @input=${(e: Event) => (this.code = (e.target as HTMLInputElement).value)}
            required
          />
        </div>
        <button class="btn btn-primary" type="submit" ?disabled=${this.busy}>
          ${this.busy ? "Activating…" : "Activate"}
        </button>
        <button class="btn" type="button" @click=${this.cancelEnroll}>Cancel</button>
      </form>
    `;
  }

  private renderEnabled() {
    return html`
      <div class="state-line">
        <span class="badge-stable">Enabled</span>
        <span class="muted">Signing in requires an authenticator code.</span>
      </div>
      <form @submit=${this.disable}>
        <div class="field">
          <label for="disable-code">Enter a current code to disable</label>
          <input
            id="disable-code"
            class="code-input"
            inputmode="numeric"
            autocomplete="one-time-code"
            pattern="[0-9]{6}"
            maxlength="6"
            .value=${this.disableCode}
            @input=${(e: Event) => (this.disableCode = (e.target as HTMLInputElement).value)}
            required
          />
        </div>
        <button class="btn btn-danger" type="submit" ?disabled=${this.busy}>
          ${this.busy ? "Disabling…" : "Disable 2FA"}
        </button>
      </form>
    `;
  }
}

/** defaultPasskeyName derives a friendly label from the platform when the user
 * leaves the name blank (e.g. "Mac passkey", "Windows passkey"). */
function defaultPasskeyName(): string {
  const ua = navigator.userAgent;
  const platform = /iPhone|iPad/.test(ua)
    ? "iPhone"
    : /Macintosh/.test(ua)
      ? "Mac"
      : /Windows/.test(ua)
        ? "Windows"
        : /Android/.test(ua)
          ? "Android"
          : "device";
  return `${platform} passkey`;
}

declare global {
  interface HTMLElementTagNameMap {
    "settings-security": SettingsSecurity;
  }
}
